import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Banknote, Key, Shield, Store, Save, EyeOff, Eye, Globe } from "lucide-react";

const TABS = [
  { k: "profile", label: "School profile", icon: Building2 },
  { k: "bank", label: "Bank account", icon: Banknote },
  { k: "operation", label: "Operation mode", icon: Store },
  { k: "tax", label: "Tax defaults", icon: Shield },
  { k: "keys", label: "Production keys", icon: Key },
];

export default function AdminConfiguration() {
  const [tab, setTab] = useState("profile");
  const [school, setSchool] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/schools/me").then(r => setSchool(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/schools/me", school);
      toast.success("Configuration saved");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const upd = (path, value) => {
    const next = { ...school };
    const parts = path.split(".");
    let ref = next;
    for (let i = 0; i < parts.length - 1; i++) {
      ref[parts[i]] = { ...(ref[parts[i]] || {}) };
      ref = ref[parts[i]];
    }
    ref[parts[parts.length - 1]] = value;
    setSchool(next);
  };

  if (!school) return <AppShell variant="admin"><div className="p-10 text-center text-ink-500">Loading…</div></AppShell>;

  const Field = ({ label, value, onChange, type = "text", testid, hint, secret = false, fieldKey }) => {
    const reveal = !secret || showSecrets[fieldKey];
    return (
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-ink-500">{label}</label>
        <div className="relative mt-1">
          <input
            data-testid={testid}
            type={reveal ? type : "password"}
            value={value || ""}
            onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
            className="w-full rounded-xl border border-border bg-white px-4 py-2.5 pr-10 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 font-mono text-sm"
          />
          {secret && (
            <button type="button" onClick={() => setShowSecrets(s => ({ ...s, [fieldKey]: !s[fieldKey] }))}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-500">
              {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          )}
        </div>
        {hint && <div className="text-[11px] text-ink-500 mt-1">{hint}</div>}
      </div>
    );
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Configuration</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">{school.name}</h1>
            <div className="text-xs text-ink-500 mt-1">Subdomain · <span className="font-mono">{school.slug}.grubpe.com</span></div>
          </div>
          <button data-testid="config-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 disabled:opacity-60">
            <Save size={16} /> {saving ? "Saving..." : "Save changes"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <aside className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-border p-2">
              {TABS.map(t => {
                const I = t.icon;
                return (
                  <button key={t.k} onClick={() => setTab(t.k)} data-testid={`config-tab-${t.k}`}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab===t.k ? 'bg-ink text-white' : 'text-ink-700 hover:bg-cream-100'}`}>
                    <I size={14} /> {t.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 bg-cream-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-ink-700"><Globe size={12}/> Subdomain plan</div>
              <div className="text-xs text-ink-600 mt-2 leading-relaxed">
                Each school gets its own subdomain pattern:
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li><span className="font-mono">{school.slug}hub.grubpe.com</span> — admin/parent</li>
                  <li><span className="font-mono">{school.slug}qr.grubpe.com</span> — QR ordering</li>
                  <li><span className="font-mono">{school.slug}pos.grubpe.com</span> — POS terminal</li>
                </ul>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9 bg-white rounded-3xl border border-border p-6 sm:p-8">
            {tab === "profile" && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold">School profile</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="School name" value={school.name} onChange={v => upd("name", v)} testid="cfg-name" />
                  <Field label="Subdomain slug" value={school.slug} onChange={v => upd("slug", v.toLowerCase())} testid="cfg-slug" hint="Lowercase, used as <slug>hub.grubpe.com" />
                  <div className="sm:col-span-2"><Field label="Address" value={school.address} onChange={v => upd("address", v)} testid="cfg-address" /></div>
                  <Field label="Phone" value={school.phone} onChange={v => upd("phone", v)} testid="cfg-phone" />
                  <Field label="Email" value={school.email} onChange={v => upd("email", v)} testid="cfg-email" type="email" />
                  <div className="sm:col-span-2"><Field label="Logo URL" value={school.logo_url} onChange={v => upd("logo_url", v)} testid="cfg-logo" /></div>
                </div>
              </div>
            )}

            {tab === "bank" && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold">School bank account</h2>
                <p className="text-sm text-ink-600">All canteen money lands here. Vendor payouts (if vendor-operated) are processed weekly from this account.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><Field label="Account holder" value={school.bank_account?.account_holder} onChange={v => upd("bank_account.account_holder", v)} testid="cfg-bank-holder" /></div>
                  <Field label="Account number" value={school.bank_account?.account_number} onChange={v => upd("bank_account.account_number", v)} testid="cfg-bank-acc" secret fieldKey="acc" />
                  <Field label="IFSC" value={school.bank_account?.ifsc} onChange={v => upd("bank_account.ifsc", v)} testid="cfg-bank-ifsc" />
                  <Field label="Bank name" value={school.bank_account?.bank_name} onChange={v => upd("bank_account.bank_name", v)} testid="cfg-bank-name" />
                  <Field label="UPI VPA" value={school.bank_account?.upi_vpa} onChange={v => upd("bank_account.upi_vpa", v)} testid="cfg-bank-upi" hint="optional" />
                </div>
              </div>
            )}

            {tab === "operation" && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold">Operation mode</h2>
                <p className="text-sm text-ink-600">Who actually runs the canteen day-to-day?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { k: "school", label: "School-operated", desc: "School staff runs the canteen. All revenue stays with the school." },
                    { k: "vendor", label: "Vendor-operated", desc: "External vendor runs it. Money lands in the school account, school settles vendor weekly." },
                  ].map(opt => (
                    <button key={opt.k} type="button" data-testid={`op-${opt.k}`} onClick={() => upd("operation_mode", opt.k)}
                      className={`text-left p-5 rounded-2xl border-2 ${school.operation_mode===opt.k ? 'border-brand bg-brand-50' : 'border-border hover:border-ink-300'}`}>
                      <div className="font-heading font-bold text-ink">{opt.label}</div>
                      <div className="text-xs text-ink-600 mt-1">{opt.desc}</div>
                    </button>
                  ))}
                </div>

                {school.operation_mode === "vendor" && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 bg-cream-100 rounded-2xl border border-border">
                    <div className="sm:col-span-2 font-heading font-bold text-ink">Vendor agreement</div>
                    <Field label="Vendor name" value={school.vendor_agreement?.vendor_name} onChange={v => upd("vendor_agreement.vendor_name", v)} testid="cfg-v-name" />
                    <Field label="Vendor contact" value={school.vendor_agreement?.vendor_contact} onChange={v => upd("vendor_agreement.vendor_contact", v)} testid="cfg-v-contact" />
                    <div className="sm:col-span-2"><Field label="Vendor address" value={school.vendor_agreement?.vendor_address} onChange={v => upd("vendor_agreement.vendor_address", v)} testid="cfg-v-address" /></div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Settlement frequency</label>
                      <select data-testid="cfg-v-freq" value={school.vendor_agreement?.settlement_frequency || "weekly"} onChange={e => upd("vendor_agreement.settlement_frequency", e.target.value)}
                        className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none">
                        <option value="weekly">Weekly</option>
                        <option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Settlement day</label>
                      <select data-testid="cfg-v-day" value={school.vendor_agreement?.settlement_day || "Monday"} onChange={e => upd("vendor_agreement.settlement_day", e.target.value)}
                        className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none">
                        {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <Field label="Vendor revenue share %" type="number" value={school.vendor_agreement?.revenue_share_pct} onChange={v => upd("vendor_agreement.revenue_share_pct", v)} testid="cfg-v-pct" hint={`School keeps ${(100 - (school.vendor_agreement?.revenue_share_pct || 85)).toFixed(0)}%`} />
                  </div>
                )}
              </div>
            )}

            {tab === "tax" && (
              <div className="space-y-4">
                <h2 className="font-heading text-xl font-bold">Tax defaults</h2>
                <p className="text-sm text-ink-600">Default GST rate when adding new menu items. Per-item override available in the Menu page.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Default GST rate %" type="number" value={school.tax_defaults?.default_gst_pct} onChange={v => upd("tax_defaults.default_gst_pct", v)} testid="cfg-tax-pct" hint="Common values: 5% for restaurant food, 12% packaged" />
                  <label className="flex items-center gap-2 p-3 bg-cream-100 rounded-xl cursor-pointer mt-6">
                    <input data-testid="cfg-tax-mrp" type="checkbox" checked={!!school.tax_defaults?.mrp_default} onChange={e => upd("tax_defaults.mrp_default", e.target.checked)} />
                    <span className="text-sm font-semibold">New items default to MRP-inclusive pricing</span>
                  </label>
                  <label className="flex items-center gap-2 p-3 bg-cream-100 rounded-xl cursor-pointer">
                    <input data-testid="cfg-tax-show" type="checkbox" checked={!!school.tax_defaults?.show_tax_breakdown} onChange={e => upd("tax_defaults.show_tax_breakdown", e.target.checked)} />
                    <span className="text-sm font-semibold">Show tax breakdown on receipts</span>
                  </label>
                </div>
              </div>
            )}

            {tab === "keys" && (
              <div className="space-y-5">
                <h2 className="font-heading text-xl font-bold">Production keys</h2>
                <p className="text-sm text-ink-600">Plug in real credentials to take Cashfree, SendGrid, Karix, EdKonnect live. Until set, the platform stays in graceful-mock mode (still fully functional for demos).</p>

                <div className="bg-cream-100 rounded-2xl p-5 border border-border">
                  <div className="flex items-center gap-2 font-heading font-bold text-ink"><Key size={16}/> Cashfree</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <Field label="App ID" value={school.production_keys?.cashfree_app_id} onChange={v => upd("production_keys.cashfree_app_id", v)} testid="key-cf-app" secret fieldKey="cf_app" />
                    <Field label="Secret key" value={school.production_keys?.cashfree_secret_key} onChange={v => upd("production_keys.cashfree_secret_key", v)} testid="key-cf-secret" secret fieldKey="cf_secret" />
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Environment</label>
                      <select data-testid="key-cf-env" value={school.production_keys?.cashfree_env || "SANDBOX"} onChange={e => upd("production_keys.cashfree_env", e.target.value)}
                        className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none">
                        <option value="SANDBOX">Sandbox</option>
                        <option value="PRODUCTION">Production</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-cream-100 rounded-2xl p-5 border border-border">
                  <div className="flex items-center gap-2 font-heading font-bold text-ink"><Key size={16}/> SendGrid</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <Field label="API key" value={school.production_keys?.sendgrid_api_key} onChange={v => upd("production_keys.sendgrid_api_key", v)} testid="key-sg" secret fieldKey="sg" />
                    <Field label="Sender email" value={school.production_keys?.sender_email} onChange={v => upd("production_keys.sender_email", v)} testid="key-sg-from" type="email" />
                    <div className="sm:col-span-2"><Field label="Daily report recipients (comma-sep)" value={school.production_keys?.admin_report_emails} onChange={v => upd("production_keys.admin_report_emails", v)} testid="key-sg-to" /></div>
                  </div>
                </div>

                <div className="bg-cream-100 rounded-2xl p-5 border border-border">
                  <div className="flex items-center gap-2 font-heading font-bold text-ink"><Key size={16}/> Karix SMS</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <Field label="API key" value={school.production_keys?.karix_api_key} onChange={v => upd("production_keys.karix_api_key", v)} testid="key-karix" secret fieldKey="karix" />
                    <Field label="Sender ID" value={school.production_keys?.karix_sender_id} onChange={v => upd("production_keys.karix_sender_id", v)} testid="key-karix-id" hint="6-char DLT-approved" />
                  </div>
                </div>

                <div className="bg-cream-100 rounded-2xl p-5 border border-border">
                  <div className="flex items-center gap-2 font-heading font-bold text-ink"><Key size={16}/> EdKonnect</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <Field label="API URL" value={school.production_keys?.edkonnect_api_url} onChange={v => upd("production_keys.edkonnect_api_url", v)} testid="key-edk-url" />
                    <Field label="API key" value={school.production_keys?.edkonnect_api_key} onChange={v => upd("production_keys.edkonnect_api_key", v)} testid="key-edk-key" secret fieldKey="edk" />
                  </div>
                </div>

                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">⚠️ Saving keys here stores them in the school record. Backend services currently read from <span className="font-mono">/app/backend/.env</span>; on next iteration we'll wire backend to prefer school-config keys when present.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
